import React, { useEffect, useRef, useState, useCallback } from 'react';

const STORAGE_KEY = 'mini_player_pos_v1';

/**
 * Hook that wires drag-to-move behaviour on a fixed-position element.
 *
 * Returns:
 *   • style       — apply to the outer container (transform + transition)
 *   • dragHandlers — spread on the *grip* element only (prevents swallowing
 *     normal taps on the rest of the bar)
 *   • dragging    — boolean for visual feedback (hover ring, etc.)
 *   • reset       — manual reset back to the default docked position
 *
 * Persistence: the latest dropped position is saved to localStorage so it
 * survives reloads. We snap to the nearest left/right edge on release so the
 * bar never ends up half-clipped, and clamp Y to keep it on-screen.
 */
const useDraggable = ({ defaultY = 0, edgePadding = 8, enabled = true, dragThreshold = 6 }) => {
  const [pos, setPos] = useState(() => {
    if (typeof window === 'undefined') return { side: 'center', y: defaultY };
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) return JSON.parse(raw);
    } catch (_) {}
    return { side: 'center', y: defaultY };
  });
  const [dragging, setDragging] = useState(false);
  // Has the pointer moved past the tap-vs-drag threshold? We only commit
  // position + suppress the child click when this flips true, so users can
  // still TAP the cover/title area to expand while dragging it to move.
  const [hasMoved, setHasMoved] = useState(false);
  const dragRef = useRef({ startX: 0, startY: 0, x: 0, y: 0, active: false });

  const onPointerDown = useCallback((e) => {
    if (!enabled) return;
    // Only respond to the primary mouse button / first touch.
    if (e.button !== undefined && e.button !== 0) return;
    // If the pointerdown lands on an interactive child (marked with
    // `data-drag-ignore`), bail out so the child's click fires normally.
    // Only the play/close/pin buttons need this — the cover+title area
    // is intentionally NOT marked so the user can drag from there too.
    if (e.target?.closest?.('[data-drag-ignore]')) return;
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      x: 0,
      y: pos.y || 0,
      active: true,
      pointerId: e.pointerId,
    };
    setHasMoved(false);
    // Use currentTarget (the element that the handler is bound to, i.e.
    // the drag handle itself) so pointer capture survives re-renders of
    // any children (like the GripVertical icon being swapped when
    // `dragging` flips true). `e.target` could be a child SVG that
    // unmounts mid-drag.
    try {
      e.currentTarget.setPointerCapture?.(e.pointerId);
    } catch { /* pointer capture is best-effort */ }
    // Subtle contact cue — 3ms buzz says "I caught your gesture". Distinct
    // from the 8ms cue that fires once we commit to drag so the two stages
    // feel separable on iOS.
    try { navigator.vibrate?.(3); } catch { /* ignore */ }
    setDragging(true);
  }, [pos.y, enabled]);

  const onPointerMove = useCallback((e) => {
    if (!dragRef.current.active) return;
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    dragRef.current.x = dx;
    dragRef.current.y = dy;
    // Only commit to a drag once past the threshold. Below it, we treat
    // the gesture as a tap so the underlying click handler (e.g. expand)
    // fires normally on release.
    if (!hasMoved && Math.hypot(dx, dy) > dragThreshold) {
      setHasMoved(true);
      // Haptic cue the instant the gesture commits to "drag" — iOS
      // Safari exposes `navigator.vibrate([...])` on the Web Vibrate API
      // for most PWAs; when unsupported (most iOS builds), the call is
      // a silent no-op so we never throw.
      try { navigator.vibrate?.(8); } catch { /* ignore */ }
    }
    setPos((p) => ({ ...p, _liveDx: dx, _liveDy: dy }));
  }, [hasMoved, dragThreshold]);

  const settle = useCallback((e) => {
    if (!dragRef.current.active) return;
    dragRef.current.active = false;
    setDragging(false);

    // If the pointer never passed the tap threshold, treat this as a
    // click — don't write a new position or snap. This is what lets the
    // big cover+title surface still respond to taps.
    if (!hasMoved) {
      setPos((p) => ({ ...p, _liveDx: 0, _liveDy: 0 }));
      return;
    }
    // Past the threshold — this WAS a drag, so kill any trailing click
    // the browser might synthesize.
    if (e?.preventDefault) e.preventDefault();

    const dx = dragRef.current.x;
    const dy = dragRef.current.y;
    const w = window.innerWidth;
    const h = window.innerHeight;

    // Pick the side based on horizontal centre of the bar after drag.
    // Anything past 60% of the screen width snaps right; otherwise left.
    const projectedCenterX = w / 2 + dx;
    const side = projectedCenterX > w * 0.6 ? 'right' : projectedCenterX < w * 0.4 ? 'left' : 'center';

    // Vertical clamp — keep at least 24px gap from the top, leave room for
    // the bottom nav at the bottom (handled by the consumer).
    const yMax = h - 80 - edgePadding;
    const newY = Math.max(-yMax, Math.min(0, (pos.y || 0) + dy));

    const next = { side, y: newY };
    setPos(next);
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch (_) {}
    // Snap-to-edge buzz — a slightly longer 12ms thump so the user
    // can tell "you docked" apart from "you started dragging" (3-8ms).
    // Skipped when the user simply releases mid-screen (no side change).
    if (side !== 'center') {
      try { navigator.vibrate?.(12); } catch { /* ignore */ }
    }
  }, [pos.y, edgePadding, hasMoved]);

  const reset = useCallback(() => {
    setPos({ side: 'center', y: defaultY });
    try { localStorage.removeItem(STORAGE_KEY); } catch (_) {}
  }, [defaultY]);

  // Effective transform — combines persisted side/y with any in-flight drag.
  const liveDx = dragRef.current.active ? dragRef.current.x : 0;
  const liveDy = dragRef.current.active ? dragRef.current.y : 0;

  let translateX = 0;
  if (pos.side === 'left') translateX = -1000; // marker — consumer pins to left edge
  else if (pos.side === 'right') translateX = 1000; // marker — consumer pins to right edge

  // While dragging we add the live delta to the persisted Y so the bar
  // tracks the pointer 1:1.
  const transform = `translate3d(${liveDx}px, ${(pos.y || 0) + liveDy}px, 0)`;

  // Listen for pointer events on the document while dragging so we don't
  // lose the pointer if it moves outside the grip element. Cleaner UX
  // than leaving the listeners on the grip itself.
  useEffect(() => {
    if (!dragging) return;
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', settle);
    window.addEventListener('pointercancel', settle);
    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', settle);
      window.removeEventListener('pointercancel', settle);
    };
  }, [dragging, onPointerMove, settle]);

  return {
    pos,           // {side, y} — consumer uses `side` to decide left/right anchoring
    transform,     // apply to outer container while dragging
    dragging,
    dragHandlers: { onPointerDown },
    reset,
  };
};

export default useDraggable;
